import React, { lazy, Suspense } from 'react'
import { Route, Switch, Redirect } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { getRouteValue } from './RouteContext'

/*
  Currently all CrowdSurf pages are displayed by having pages embedded into A20M websites dynamically.
  The routing rules in the A20M app are using a catch-all wildcard after https://advertising.amazon.com/partners/directory/**
  to point to the root of our app.
  We ran into an issue that specific chars (. in this case) break their routing rules. (https://t.corp.amazon.com/P46202781)
  The issue has been resolved but if there's any plan to add new rules on our end that would potentially introduce special chars (eg. in slug),
  please make sure to test it first. If it doesn't work, please cut a SIM to the A20M team.
 */

const App = lazy(() => import('App').then(module => ({ default: module.App })))
const Directory = lazy(() => import('views/directory/directory').then(module => ({ default: module.Directory })))
const PartnerDetails = lazy(() => import('views/partner_details/partner_details').then(module => ({ default: module.PartnerDetails })))
const ErrorPage = lazy(() => import('@amzn/aapn-react-components').then(module => ({ default: module.ErrorPage })))
const PartnerComparisonDetails = lazy(() =>
  import('views/partner_comparison_details/partner_comparison_details').then(module => ({ default: module.PartnerComparisonDetails })),
)
const Leads = lazy(() => import('views/leads/leads').then(module => ({ default: module.Leads })))

export const Routes = (): JSX.Element => {
  const { t } = useTranslation()

  return (
    <App>
      <Suspense fallback={React.Fragment}>
        <Switch>
          <Redirect exact from="/" to="/partners/directory" />
          {/* Removes duplicate slashes */}
          <Route exact strict path={'(.*//+.*)'} render={({ location }) => <Redirect to={location.pathname.replace(/\/\/+/g, '/')} />} />
          <Route exact path={['/:locale/partners/directory', '/partners/directory']} component={Directory} />
          <Route
            exact
            path={[
              '/:locale/partners/directory/details/:id',
              '/partners/directory/details/:id',
              '/:locale/partners/directory/details/:id/:name',
              '/partners/directory/details/:id/:name',
            ]}
            render={props => <PartnerDetails {...props} />}
          />
          <Route exact path={['/:locale/partners/directory/compare', '/partners/directory/compare']} component={PartnerComparisonDetails} />
          <Route
            exact
            path={[
              '/:locale/partners/directory/contact/:id',
              '/partners/directory/contact/:id',
              '/:locale/partners/directory/contact/:id/:name',
              '/partners/directory/contact/:id/:name',
            ]}
            render={props => <Leads {...props} />}
          />
          <Route
            render={({ location }) => (
              <ErrorPage
                location={{
                  state: {
                    title: t('SHARED:ERROR_PAGE:TITLE'),
                    message: t('SHARED:ERROR_PAGE:MESSAGE'),
                    routeDisplayText: t('SHARED:ERROR_PAGE:ROUTE_DISPLAY_TEXT'),
                    routePath: getRouteValue(location.pathname),
                  },
                }}
              />
            )}
          />
        </Switch>
      </Suspense>
    </App>
  )
}
